import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { ScoreRing } from "@/lib/match";
import { Printer, CheckCircle2, HelpCircle, Repeat, AlertTriangle, Lightbulb, MessageSquareText } from "lucide-react";

const BREAKDOWN = [["education", "Required Education", 20], ["license_cert", "License / Certification", 25], ["experience", "Relevant Experience", 20], ["skills", "Required Skills", 15], ["population_setting", "Population / Setting", 10], ["location_availability", "Location / Availability", 5], ["preferred", "Preferred Qualifications", 5]];
const listify = (a) => (Array.isArray(a) ? a : []);

const List = ({ icon: Icon, title, items, color }) => (
  <div className="break-inside-avoid">
    <div className={`flex items-center gap-2 font-semibold text-sm mb-1.5 ${color}`}><Icon size={15} /> {title}</div>
    {listify(items).length ? <ul className="space-y-1 text-sm text-slate-700">{listify(items).map((it, i) => <li key={i} className="flex gap-1.5"><span className="text-slate-400">•</span>{it}</li>)}</ul> : <p className="text-sm text-slate-400 italic">None listed.</p>}
  </div>
);

export default function SubmissionPacket() {
  const { candidateId, positionId } = useParams();
  const [c, setC] = useState(null);
  const [pos, setPos] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/candidates/${candidateId}`).then((r) => setC(r.data)).catch(() => setError(true));
    api.get(`/positions/${positionId}`).then((r) => setPos(r.data)).catch(() => setError(true));
  }, [candidateId, positionId]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-center p-8"><div><p className="font-head font-bold text-lg">Packet unavailable</p><p className="text-muted-foreground text-sm mt-1">This candidate or position could not be found.</p></div></div>;
  if (!c || !pos) return <div className="p-8">Loading packet…</div>;
  const m = (c.matches || []).find((x) => x.position_id === positionId);
  if (!m) return <div className="min-h-screen flex items-center justify-center text-center p-8"><div><p className="font-head font-bold text-lg">No match found</p><p className="text-muted-foreground text-sm mt-1">This candidate has not been scored against this position yet. Re-run matching from the position page.</p></div></div>;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <span className="text-sm text-slate-500">Candidate Submission Packet</span>
        <button onClick={() => window.print()} data-testid="print-packet-btn" className="flex items-center gap-2 bg-[hsl(var(--teal))] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[hsl(var(--teal))]/90 transition-colors">
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white my-6 print:my-0 shadow-sm print:shadow-none" data-testid="packet-sheet">
        {/* Header */}
        <div className="bg-[hsl(var(--teal))] text-white px-10 py-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="bg-white rounded-md px-2 py-1.5 inline-flex"><img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_tag-candidate-portal/artifacts/swk6w00s_IMG_9395.png" alt="The Allicock Group" className="h-7 w-auto object-contain" /></div>
              <span className="font-head font-extrabold text-lg tracking-tight">TAG Recruit AI</span>
            </div>
            <div className="text-white/70 text-xs mt-1 uppercase tracking-widest">The Allicock Group · Confidential Candidate Submission</div>
          </div>
          <div className="text-right text-xs text-white/70">{new Date().toLocaleDateString()}</div>
        </div>

        <div className="px-10 py-8 space-y-8">
          {/* Candidate + position + score */}
          <div className="flex items-start justify-between gap-6 border-b border-slate-200 pb-6">
            <div>
              <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">{c.name || "Candidate"}</h1>
              <p className="text-slate-600 mt-1">{c.current_title}{c.city && ` · ${c.city}, ${c.state}`}</p>
              <p className="text-slate-500 text-sm mt-1">{[c.email, c.phone].filter(Boolean).join(" · ")}</p>
              <div className="mt-4 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-400">Submitted for</div>
                <div className="font-semibold text-slate-800">{pos.primary_title}{pos.client && ` — ${pos.client}`}</div>
                <div className="text-slate-500">{[pos.school_location || pos.location, pos.employment_type].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div className="text-center shrink-0">
              <ScoreRing score={m.score || 0} size={92} />
              <div className="mt-2 text-xs uppercase tracking-wide text-slate-500 capitalize">{m.classification || "not scored"}</div>
            </div>
          </div>

          {/* Snapshot */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm break-inside-avoid">
            {[["Highest Degree", `${c.highest_degree || ""} ${c.field_of_study ? `· ${c.field_of_study}` : ""}`.trim()],
              ["Years Experience", c.years_experience ? `${c.years_experience} years` : ""],
              ["Licenses", listify(c.licenses).join(", ")], ["Certifications", listify(c.certifications).join(", ")],
              ["Languages", listify(c.languages).join(", ")], ["Populations Served", listify(c.populations_served).join(", ")]
            ].map(([k, v]) => (
              <div key={k}><div className="text-xs uppercase tracking-wide text-slate-400">{k}</div><div className="text-slate-800">{v || <span className="italic text-slate-400">not found on résumé</span>}</div></div>
            ))}
          </div>

          {c.professional_summary && (
            <div className="break-inside-avoid">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Professional Summary</div>
              <p className="text-sm text-slate-700 leading-relaxed">{c.professional_summary}</p>
            </div>
          )}

          {/* Score breakdown */}
          <div className="break-inside-avoid">
            <h2 className="font-head font-bold text-lg text-slate-900 mb-3">Match Score Breakdown</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
              {BREAKDOWN.map(([k, label, max]) => {
                const val = m.breakdown?.[k] ?? 0;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-800">{val}/{max}</span></div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[hsl(var(--teal))] rounded-full" style={{ width: `${max ? (val / max) * 100 : 0}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Explainability */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            <List icon={CheckCircle2} color="text-emerald-600" title="Qualifications met" items={m.qualifications_met} />
            <List icon={HelpCircle} color="text-amber-600" title="Not found on résumé — verify" items={m.qualifications_not_found} />
            <List icon={Repeat} color="text-blue-600" title="Transferable skills" items={m.transferable_skills} />
            <List icon={AlertTriangle} color="text-red-600" title="Concerns" items={m.concerns} />
          </div>

          {m.recommended_next_step && (
            <div className="bg-[hsl(var(--teal))]/8 border border-[hsl(var(--teal))]/20 rounded-lg p-4 break-inside-avoid">
              <div className="flex items-center gap-2 text-[hsl(var(--teal))] font-semibold text-sm mb-1"><Lightbulb size={15} /> Recommended next step</div>
              <p className="text-sm text-slate-700">{m.recommended_next_step}</p>
            </div>
          )}

          {listify(m.screening_questions).length > 0 && (
            <div className="break-inside-avoid">
              <div className="flex items-center gap-2 font-semibold text-sm mb-2 text-slate-800"><MessageSquareText size={15} className="text-[hsl(var(--teal))]" /> Recommended screening questions</div>
              <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">{listify(m.screening_questions).map((q, i) => <li key={i}>{q}</li>)}</ol>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 text-[11px] text-slate-400 leading-relaxed break-inside-avoid">
            Prepared by TAG Recruit AI for The Allicock Group. Fields labeled "not found on résumé" indicate information not present in the source document and should be verified with the candidate; absence on the résumé does not confirm the candidate lacks the qualification. Confidential — for client evaluation only.
          </div>
        </div>
      </div>
    </div>
  );
}
